// Configuration assistant (docs/data-sources-v2-spec.md §7): the side panel of
// the data-source paths. Documents in (URL, PDF, OpenAPI, text, screenshot),
// proposals, explanations of failed tests and answers out — every change
// comes back as a patch the page applies with one click (« Appliquer »).
// A failed test on the page is explained without being asked.
// API: /api/integrations/connectors/assistant/{documents, propose, explain, chat}.
// Links: a document's URL renders as a link only when http(s) (webHref).

import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
    Check, FileUp, Link as LinkIcon, LoaderCircle, Send, Sparkles, X,
} from 'lucide-react';
import { Badge, Banner, ConfirmDialog, IconChip } from '../design/ui';
import { webHref } from '../utils/links';
import './ConnectorAssistant.css';

const API = '/api/integrations/connectors/assistant';
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });

const readFile = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    if (file.type === 'application/pdf' || file.type.startsWith('image/')) reader.readAsDataURL(file); else reader.readAsText(file);
});

const ConnectorAssistant = ({ draftId, draft, lastTest, onApply, stepHint }) => {
    const { t } = useTranslation();
    const [documents, setDocuments] = useState([]);
    const [url, setUrl] = useState('');
    const [text, setText] = useState('');
    const [messages, setMessages] = useState([]);
    const [question, setQuestion] = useState('');
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState(null);
    const [removing, setRemoving] = useState(null);
    const fileRef = useRef(null);
    const endRef = useRef(null);

    const loadDocuments = useCallback(async () => {
        if (!draftId) return;
        try {
            const res = await axios.get(`${API}/documents?draft_id=${encodeURIComponent(draftId)}`, authHeaders());
            setDocuments(res.data.documents || []);
        } catch (e) { setDocuments([]); }
    }, [draftId]);

    useEffect(() => { loadDocuments(); }, [loadDocuments]);
    useEffect(() => { endRef.current?.scrollIntoView?.({ block: 'nearest' }); }, [messages]);

    const fail = (e, fallback) => { const d = e?.response?.data?.detail; setError(typeof d === 'string' ? d : fallback); };
    const push = (message) => setMessages((m) => [...m, message]);

    const addDocument = async (payload) => {
        setBusy('doc');
        setError(null);
        try {
            const res = await axios.post(`${API}/documents`, { draft_id: draftId, ...payload }, authHeaders());
            setDocuments((d) => [...d, res.data]);
            push({ role: 'assistant', text: t('assistant.docAdded', 'Document lu : « {{title}} » ({{n}} extraits). Je peux proposer la configuration.', { title: res.data.title, n: res.data.chunks }) });
        } catch (e) {
            fail(e, t('assistant.docError', 'Le document n’a pas pu être lu.'));
        } finally {
            setBusy(null);
        }
    };

    const onFile = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        try {
            const content = await readFile(file);
            if (file.type === 'application/pdf') await addDocument({ kind: 'pdf', file_b64: content.split(',')[1], filename: file.name });
            else if (file.type.startsWith('image/')) await addDocument({ kind: 'image', image_b64: content, filename: file.name });
            else await addDocument({ kind: 'text', text: content, filename: file.name });
        } catch (e) {
            setError(t('assistant.docError', 'Le document n’a pas pu être lu.'));
        } finally {
            if (fileRef.current) fileRef.current.value = '';
        }
    };

    const removeDocument = async (id) => {
        try {
            await axios.delete(`${API}/documents/${encodeURIComponent(id)}`, authHeaders());
            setDocuments((d) => d.filter((x) => x.id !== id));
        } catch (e) { /* stays listed */ }
        setRemoving(null);
    };

    const propose = async () => {
        setBusy('propose');
        setError(null);
        try {
            const res = await axios.post(`${API}/propose`, { draft_id: draftId, draft }, authHeaders());
            const count = Object.keys(res.data.patch || {}).length;
            push({
                role: 'assistant',
                text: count
                    ? t('assistant.proposalReady', 'Configuration proposée à partir de {{docs}} ({{n}} réglages).', { docs: (res.data.sources || []).join(', '), n: count })
                    : t('assistant.proposalEmpty', 'Je n’ai rien trouvé d’exploitable dans les documents.'),
                notes: res.data.notes,
                errors: res.data.errors,
                patch: count ? res.data.patch : null,
                sample: res.data.sample ?? null,
            });
        } catch (e) {
            fail(e, t('assistant.proposeError', 'La proposition a échoué.'));
        } finally {
            setBusy(null);
        }
    };

    const explain = useCallback(async (test) => {
        setBusy('explain');
        setError(null);
        try {
            const res = await axios.post(`${API}/explain`, { draft_id: draftId, draft, reason: test.reason, error: test.error, status: test.status, identifiers: test.identifiers || {}, raw: test.raw ?? null, steps: test.steps || null }, authHeaders());
            push({ role: 'assistant', text: res.data.explanation, patch: res.data.fix || null, confidence: res.data.confidence });
        } catch (e) {
            fail(e, t('assistant.explainError', 'L’explication a échoué.'));
        } finally {
            setBusy(null);
        }
    }, [draftId, draft, t]);

    // A failed test just happened on the page: explain it without being asked.
    const lastExplained = useRef(null);
    useEffect(() => {
        if (lastTest && lastTest.ok === false && lastTest.reason && lastTest.stamp !== lastExplained.current) {
            lastExplained.current = lastTest.stamp;
            explain(lastTest);
        }
    }, [lastTest, explain]);

    const ask = async () => {
        const q = question.trim();
        if (!q) return;
        setQuestion('');
        push({ role: 'user', text: q });
        setBusy('chat');
        setError(null);
        try {
            const history = messages.slice(-6).map((m) => ({ role: m.role, text: m.text }));
            const res = await axios.post(`${API}/chat`, { draft_id: draftId, question: q, draft, last_test: lastTest ? { found: lastTest.found, reason: lastTest.reason, error: lastTest.error, status: lastTest.status } : null, history }, authHeaders());
            push({ role: 'assistant', text: res.data.answer, patch: res.data.patch || null, sources: res.data.sources });
        } catch (e) {
            fail(e, t('assistant.chatError', 'Je n’ai pas pu répondre.'));
        } finally {
            setBusy(null);
        }
    };

    const apply = (index) => {
        const message = messages[index];
        onApply?.(message.patch, message.sample ?? null);
        setMessages((m) => m.map((x, i) => (i === index ? { ...x, applied: true } : x)));
    };

    return (
        <aside className="u-card ca-panel" aria-label={t('assistant.title', 'Assistant')}>
            <div className="ca-head">
                <IconChip icon={Sparkles} />
                <div className="ca-head__text">
                    <h2 className="ca-head__title">{t('assistant.title', 'Assistant')}</h2>
                    <p className="ca-head__desc">{t('dataToolsUi.assistant.desc', 'Lit la documentation de votre système et remplit la configuration.')}</p>
                </div>
                {stepHint && <Badge tone="accent">{stepHint}</Badge>}
            </div>

            <div className="ca-docs">
                <div className="ca-docs__head">
                    <span className="u-overline">{t('assistant.docs', 'Documentation')}</span>
                    <span className="ca-muted">{t('assistant.docsHint', 'URL, PDF, OpenAPI, texte, capture d’écran')}</span>
                </div>
                {documents.length > 0 && (
                    <ul className="ca-doc-list">
                        {documents.map((doc) => {
                            const href = webHref(doc.url);
                            return (
                                <li key={doc.id} className="ca-doc">
                                    <span className="u-tag">{doc.kind}</span>
                                    {href
                                        ? <a className="ca-doc__title u-link" href={href} target="_blank" rel="noopener noreferrer" title={href}>{doc.title || href}</a>
                                        : <span className="ca-doc__title" title={doc.title}>{doc.title}</span>}
                                    <button type="button" className="u-btn u-btn--ghost u-btn--icon u-btn--sm" onClick={() => setRemoving(doc)}
                                        title={t('assistant.removeDoc', 'Retirer')} aria-label={t('dataToolsUi.assistant.removeDoc', 'Retirer « {{title}} »', { title: doc.title })}>
                                        <X aria-hidden="true" />
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
                <div className="ca-row">
                    <div className="u-input-group">
                        <input className="u-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://docs.mon-wms.com/api"
                            aria-label={t('dataToolsUi.assistant.docUrl', 'Adresse de la documentation')} />
                        <button type="button" className="u-input-group__btn" disabled={busy === 'doc' || !url.trim() || !draftId}
                            onClick={() => { addDocument({ kind: 'url', url: url.trim() }); setUrl(''); }}
                            title={t('dataToolsUi.assistant.addUrl', 'Lire cette adresse')} aria-label={t('dataToolsUi.assistant.addUrl', 'Lire cette adresse')}>
                            <LinkIcon aria-hidden="true" />
                        </button>
                    </div>
                    <input ref={fileRef} type="file" accept=".pdf,.json,.yaml,.yml,.txt,.md,image/*" hidden onChange={onFile} />
                    <button type="button" className="u-btn u-btn--secondary u-btn--icon" disabled={busy === 'doc' || !draftId} onClick={() => fileRef.current?.click()}
                        title={t('assistant.upload', 'Déposer un fichier (PDF, OpenAPI, capture)')} aria-label={t('assistant.upload', 'Déposer un fichier (PDF, OpenAPI, capture)')}>
                        <FileUp aria-hidden="true" />
                    </button>
                </div>
                <textarea className="u-textarea ca-paste" rows={3} value={text} onChange={(e) => setText(e.target.value)} spellCheck={false}
                    aria-label={t('assistant.pastePlaceholder', 'Ou collez ici un extrait de documentation, une commande cURL ou une réponse d’exemple')}
                    placeholder={t('assistant.pastePlaceholder', 'Ou collez ici un extrait de documentation, une commande cURL ou une réponse d’exemple')} />
                <div className="ca-row ca-row--end">
                    <button type="button" className="u-btn u-btn--ghost u-btn--sm" disabled={busy === 'doc' || !text.trim() || !draftId}
                        onClick={() => { addDocument({ kind: 'text', text }); setText(''); }}>
                        {t('assistant.addText', 'Ajouter')}
                    </button>
                    <button type="button" className="u-btn u-btn--primary u-btn--sm" disabled={busy === 'propose' || documents.length === 0} onClick={propose}>
                        <Sparkles aria-hidden="true" /> {t('assistant.propose', 'Proposer la configuration')}
                    </button>
                </div>
            </div>

            {error && <div className="ca-pad"><Banner tone="danger">{error}</Banner></div>}

            <div className="ca-thread" aria-live="polite">
                {messages.length === 0 && <p className="ca-muted ca-welcome">{t('assistant.welcome', 'Déposez la documentation de votre système et je remplis la configuration. Posez-moi vos questions : « pourquoi le statut n’est-il pas rempli ? », « comment chercher par e-mail ? »')}</p>}
                {messages.map((m, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={i} className={`ca-msg ca-msg--${m.role}`}>
                        <div className="ca-msg__text">{m.text}</div>
                        {m.notes?.length > 0 && <ul className="ca-notes">{m.notes.map((n) => <li key={n}>{n}</li>)}</ul>}
                        {m.errors?.length > 0 && <div className="ca-msg__error">{m.errors.join(' · ')}</div>}
                        {m.sources?.length > 0 && <div className="ca-muted">{t('assistant.sources', 'D’après')} : {m.sources.join(', ')}</div>}
                        {m.patch && (
                            <div className="ca-patch">
                                <span className="ca-patch__fields" title={Object.keys(m.patch).join(', ')}>
                                    {Object.keys(m.patch).map((k) => k.replace(/^spec\.steps\.0\./, '')).join(', ')}
                                </span>
                                {m.applied
                                    ? <Badge tone="success" icon={Check}>{t('assistant.applied', 'appliqué')}</Badge>
                                    : (
                                        <button type="button" className="u-btn u-btn--primary u-btn--sm" onClick={() => apply(i)}>
                                            <Check aria-hidden="true" /> {t('assistant.apply', 'Appliquer')}
                                        </button>
                                    )}
                            </div>
                        )}
                    </div>
                ))}
                {busy && busy !== 'doc' && (
                    <div className="ca-muted ca-thinking"><LoaderCircle className="ca-spin" aria-hidden="true" /> {t('assistant.thinking', 'Je lis…')}</div>
                )}
                <div ref={endRef} />
            </div>

            <div className="ca-ask">
                <input className="u-input" value={question} onChange={(e) => setQuestion(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') ask(); }}
                    placeholder={t('assistant.askPlaceholder', 'Une question sur cette source ?')} aria-label={t('assistant.askPlaceholder', 'Une question sur cette source ?')} />
                <button type="button" className="u-btn u-btn--secondary u-btn--icon" disabled={busy === 'chat' || !question.trim()} onClick={ask}
                    title={t('dataToolsUi.assistant.send', 'Envoyer')} aria-label={t('dataToolsUi.assistant.send', 'Envoyer')}>
                    <Send aria-hidden="true" />
                </button>
            </div>

            {removing && (
                <ConfirmDialog
                    title={t('dataToolsUi.assistant.removeTitle', 'Retirer « {{title}} » ?', { title: removing.title })}
                    message={t('dataToolsUi.assistant.removeMessage', 'L’assistant ne s’en servira plus pour cette source. Vous pourrez l’ajouter de nouveau.')}
                    confirmLabel={t('assistant.removeDoc', 'Retirer')}
                    cancelLabel={t('common.cancel', 'Annuler')}
                    onConfirm={() => removeDocument(removing.id)}
                    onCancel={() => setRemoving(null)}
                />
            )}
        </aside>
    );
};

export default ConnectorAssistant;
